import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { env } from '../utils/env';
import { notifications } from '@mantine/notifications';

// Cache for storing ETags
const etagCache = new Map<string, string>();

export interface Settlement {
    id: number;
    group_id: number;
    from_user_id: string;
    from_user_name: string;
    to_user_id: string;
    to_user_name: string;
    amount: string;
    currency_code: string;
    note?: string;
    payment_method?: string;
    payment_reference?: string;
    status: string;
    created_at: string;
}

interface SettlementCreateParams {
    from_user_id: string;
    to_user_id: string;
    amount: number;
    currency_code: string;
    note?: string;
    payment_method?: string;
    payment_reference?: string;
}

interface SettlementUpdateParams {
    note?: string;
    payment_method?: string;
    payment_reference?: string;
}

interface UseSettlementsProps {
    groupId: number;
    accessToken: string;
    options?: {
        isVisible?: boolean;
    };
}

export function useSettlements({ groupId, accessToken, options = {} }: UseSettlementsProps) {
    const { isVisible = true } = options;
    const queryClient = useQueryClient();
    const settlementsQueryKey = ['settlements', groupId];

    // Main query to fetch settlements
    const {
        data: settlements = [],
        isLoading: loading,
        error: queryError,
        refetch: getSettlements
    } = useQuery({
        queryKey: settlementsQueryKey,
        queryFn: async (): Promise<Settlement[]> => {
            try {
                console.log('[getSettlements] Fetching settlements (including cancelled)');

                // Add query parameter to include all statuses
                const url = `${env.API_URL}/api/groups/${groupId}/settlements?include_cancelled=true`;

                const headers: Record<string, string> = {
                    'Authorization': `Bearer ${accessToken}`,
                };

                // Add If-None-Match header if we have a cached ETag
                const cacheKey = `settlements_${groupId}`;
                const cachedETag = etagCache.get(cacheKey);
                if (cachedETag) {
                    headers['If-None-Match'] = cachedETag;
                }

                const response = await fetch(url, {
                    headers,
                    cache: 'no-cache', // Forces validation with server
                });

                // If we get a 304 Not Modified, use the cached data
                if (response.status === 304) {
                    console.log(`Using cached settlements data for group ${groupId} (304 Not Modified)`);
                    return queryClient.getQueryData<Settlement[]>(settlementsQueryKey) || [];
                }

                // Store the new ETag for future requests
                const newETag = response.headers.get('ETag');
                if (newETag) {
                    etagCache.set(cacheKey, newETag);
                }

                if (!response.ok) {
                    const errorText = await response.text();
                    console.error('[getSettlements] Failed to fetch settlements:', {
                        status: response.status,
                        error: errorText
                    });
                    throw new Error(errorText || `Failed to fetch settlements: ${response.status}`);
                }

                const data = await response.json();
                console.log('[getSettlements] Fetched settlements:', data?.length || 0,
                    'including', data.filter((s: Settlement) => s.status === 'cancelled').length, 'cancelled');

                return data || [];
            } catch (err: any) {
                console.error('[getSettlements] Error:', err);
                throw new Error(err.message || 'Failed to load settlements');
            }
        },
        enabled: isVisible && !!accessToken && !!groupId,
        staleTime: 300000, // 5 minutes
    });

    // Convert React Query error to string format for API compatibility
    const error = queryError ? (queryError instanceof Error ? queryError.message : String(queryError)) : null;

    // Query to fetch a single settlement by ID
    const getSettlement = useCallback(async (settlementId: number) => {
        // Use React Query's query client to fetch a single settlement
        try {
            const response = await fetch(`${env.API_URL}/api/groups/${groupId}/settlements/${settlementId}`, {
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(errorText || `Failed to fetch settlement: ${response.status}`);
            }

            return await response.json();
        } catch (err: any) {
            console.error(`Error fetching settlement ${settlementId}:`, err);
            throw new Error(err.message || 'Failed to load settlement');
        }
    }, [accessToken, groupId]);

    // Create settlement mutation
    const createSettlementMutation = useMutation({
        mutationFn: async (data: SettlementCreateParams): Promise<Settlement> => {
            console.log('[createSettlement] Creating settlement:', JSON.stringify(data, null, 2));

            const response = await fetch(`${env.API_URL}/api/groups/${groupId}/settlements`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data),
            });

            console.log(`[createSettlement] Response status: ${response.status}`);

            let responseData;
            try {
                responseData = await response.json();
                console.log('[createSettlement] Response data:', responseData);
            } catch (parseError) {
                console.error('[createSettlement] Error parsing response:', parseError);
                throw new Error('Invalid response from server');
            }

            if (!response.ok) {
                let errorMessage = 'Failed to create settlement';
                if (responseData) {
                    if (Array.isArray(responseData.detail)) {
                        // Handle validation errors
                        errorMessage = responseData.detail
                            .map((error: any) => `${error.msg} (${error.loc.join('.')})`)
                            .join(', ');
                    } else if (responseData.detail) {
                        errorMessage = responseData.detail;
                    } else if (typeof responseData === 'string') {
                        errorMessage = responseData;
                    }
                }
                throw new Error(errorMessage);
            }

            return responseData;
        },
        onSuccess: () => {
            // Invalidate settlements query to trigger a refetch
            queryClient.invalidateQueries({ queryKey: settlementsQueryKey });

            notifications.show({
                title: 'Success',
                message: 'Settlement created successfully',
                color: 'green',
            });
        },
        onError: (err: any) => {
            console.error('[createSettlement] Error:', err);

            notifications.show({
                title: 'Error',
                message: err.message || 'Failed to create settlement',
                color: 'red',
            });
        },
    });

    // Update settlement mutation
    const updateSettlementMutation = useMutation({
        mutationFn: async ({
            settlementId,
            data
        }: {
            settlementId: number,
            data: SettlementUpdateParams
        }): Promise<Settlement> => {
            const response = await fetch(`${env.API_URL}/api/groups/${groupId}/settlements/${settlementId}`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(data),
            });

            if (!response.ok) {
                let errorMessage = 'Failed to update settlement';
                try {
                    const errorData = await response.json();
                    errorMessage = errorData.detail || errorMessage;
                } catch {
                    // If response is not valid JSON, use the default message
                }
                throw new Error(errorMessage);
            }

            return await response.json();
        },
        onSuccess: () => {
            // Invalidate settlements query to trigger a refetch
            queryClient.invalidateQueries({ queryKey: settlementsQueryKey });

            notifications.show({
                title: 'Success',
                message: 'Settlement updated successfully',
                color: 'green',
            });
        },
        onError: (err: any) => {
            console.error('[updateSettlement] Error:', err);

            notifications.show({
                title: 'Error',
                message: err.message || 'Failed to update settlement',
                color: 'red',
            });
        },
    });

    // Delete settlement mutation
    const deleteSettlementMutation = useMutation({
        mutationFn: async (settlementId: number): Promise<boolean> => {
            console.log(`[deleteSettlement] Attempting to delete settlement ${settlementId} from group ${groupId}`);

            if (!settlementId || isNaN(settlementId)) {
                console.error(`[deleteSettlement] Invalid settlement ID: ${settlementId}, typeof: ${typeof settlementId}`);
                throw new Error(`Cannot delete settlement: Invalid ID provided`);
            }

            console.log(`[deleteSettlement] Settlement ID validated: ${settlementId}, making API request...`);

            const response = await fetch(`${env.API_URL}/api/groups/${groupId}/settlements/${settlementId}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                },
            });

            console.log(`[deleteSettlement] Received response status: ${response.status}`);

            let errorData = null;
            let errorText = '';

            try {
                const contentType = response.headers.get('content-type');
                console.log(`[deleteSettlement] Response content-type: ${contentType}`);

                if (contentType && contentType.includes('application/json')) {
                    errorData = await response.json();
                    console.log('[deleteSettlement] Response data:', JSON.stringify(errorData, null, 2));
                } else if (!response.ok) {
                    errorText = await response.text();
                    console.log('[deleteSettlement] Response text:', errorText);
                }
            } catch (parseError) {
                console.log('[deleteSettlement] No JSON response body or parsing error:', parseError);
            }

            // Only treat as "already cancelled" if we have a specific error about it being already cancelled
            const isAlreadyCancelledError =
                !response.ok &&
                errorData?.detail &&
                (errorData.detail.startsWith('Settlement') &&
                    errorData.detail.toLowerCase().includes('already cancelled'));

            if (isAlreadyCancelledError) {
                console.log('[deleteSettlement] Settlement is already cancelled, refreshing UI state');
                return true;
            }

            // Handle case where settlement is not found specifically
            const isNotFoundError =
                !response.ok &&
                errorData?.detail &&
                errorData.detail.toLowerCase().includes('settlement') &&
                errorData.detail.toLowerCase().includes('not found');

            if (isNotFoundError) {
                console.error(`[deleteSettlement] Settlement ${settlementId} not found. It may have been deleted already.`);
                return true;
            }

            if (!response.ok) {
                let errorMessage = 'Failed to delete settlement';
                if (errorData?.detail) {
                    errorMessage = errorData.detail;
                } else if (errorText) {
                    errorMessage = errorText;
                }

                console.error(`[deleteSettlement] Backend error during deletion: ${errorMessage}`);
                throw new Error(`Failed to delete settlement ${settlementId}: ${errorMessage}`);
            }

            console.log('[deleteSettlement] Successfully deleted settlement');
            return true;
        },
        onSuccess: () => {
            // Invalidate settlements query to trigger a refetch
            queryClient.invalidateQueries({ queryKey: settlementsQueryKey });

            notifications.show({
                title: 'Success',
                message: 'Settlement deleted successfully',
                color: 'green',
            });
        },
        onError: (err: any) => {
            console.error('[deleteSettlement] Error:', err);

            notifications.show({
                title: 'Error',
                message: err.message || 'Failed to delete settlement',
                color: 'red',
            });
        },
    });

    // Helper function to find settlement by user IDs and amount
    const findSettlementByUsers = useCallback(async (fromUserId: string, toUserId: string, amount: string) => {
        try {
            // Ensure we have the latest settlements data
            await getSettlements();

            // Get settlements from cache
            const allSettlements = queryClient.getQueryData<Settlement[]>(settlementsQueryKey) || [];

            console.log('[findSettlementByUsers] Looking for settlement with:', { fromUserId, toUserId, amount });
            console.log('[findSettlementByUsers] Found', allSettlements.length, 'settlements to search through');

            // Convert search amount to number for comparison
            const searchAmount = parseFloat(amount);

            // Find settlement that matches the criteria
            const match = allSettlements.find((s: Settlement) => {
                // Convert settlement amount to number
                const settlementAmount = parseFloat(s.amount);

                // Compare user IDs and amounts (as numbers)
                return (
                    (s.from_user_id === fromUserId &&
                        s.to_user_id === toUserId &&
                        Math.abs(settlementAmount - searchAmount) < 0.01) ||
                    (s.from_user_id === toUserId &&
                        s.to_user_id === fromUserId &&
                        Math.abs(settlementAmount - searchAmount) < 0.01)
                );
            });

            if (match) {
                console.log('[findSettlementByUsers] Found matching settlement:', match);
            } else {
                console.log('[findSettlementByUsers] No matching settlement found.');

                // More detailed debug info when settlements are found but none match
                if (allSettlements.length > 0) {
                    console.log("[findSettlementByUsers] Detailed match failure analysis:");
                    console.log("Looking for:", {
                        fromUserId,
                        toUserId,
                        amount,
                        searchAmount
                    });

                    // Log the first few settlements with detailed comparison info
                    allSettlements.slice(0, 3).forEach((s: Settlement, i: number) => {
                        const settlementAmount = parseFloat(s.amount);
                        console.log(`[findSettlementByUsers] Settlement ${i}:`, {
                            id: s.id,
                            from: s.from_user_id,
                            to: s.to_user_id,
                            amount: s.amount,
                            amountAsNumber: settlementAmount,
                            status: s.status,
                            userMatch: (s.from_user_id === fromUserId && s.to_user_id === toUserId) ||
                                (s.from_user_id === toUserId && s.to_user_id === fromUserId),
                            amountMatch: Math.abs(settlementAmount - searchAmount) < 0.01,
                            amountDiff: Math.abs(settlementAmount - searchAmount)
                        });
                    });
                }
            }

            return match || null;
        } catch (error) {
            console.error('Error finding settlement by users:', error);
            return null;
        }
    }, [getSettlements, queryClient, settlementsQueryKey]);

    // API compatibility wrapper functions
    const createSettlement = async (data: SettlementCreateParams): Promise<Settlement | null> => {
        try {
            return await createSettlementMutation.mutateAsync(data);
        } catch (error) {
            return null;
        }
    };

    const updateSettlement = async (settlementId: number, data: SettlementUpdateParams): Promise<Settlement | null> => {
        try {
            return await updateSettlementMutation.mutateAsync({ settlementId, data });
        } catch (error) {
            return null;
        }
    };

    const deleteSettlement = async (settlementId: number): Promise<boolean> => {
        try {
            return await deleteSettlementMutation.mutateAsync(settlementId);
        } catch (error) {
            return false;
        }
    };

    return {
        settlements,
        loading,
        error,
        getSettlements,
        getSettlement,
        createSettlement,
        updateSettlement,
        deleteSettlement,
        findSettlementByUsers
    };
} 